import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import ComicCard from '@/components/feature/ComicCard';
import { comics, siteInfo } from '@/mocks/comics';

export default function ComicDetail() {
  const { id } = useParams<{ id: string }>();
  const comic = comics.find((c) => c.id === Number(id));

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-background-100 flex items-center justify-center">
              <i className="ri-error-warning-line text-4xl text-foreground-300"></i>
            </div>
            <h2 className="text-xl font-heading font-bold text-foreground-900 mb-2">
              漫画未找到
            </h2>
            <p className="text-foreground-500 mb-6">抱歉，您要查找的漫画不存在或已下架</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedComics = comics
    .filter((c) => c.id !== comic.id && c.category === comic.category)
    .slice(0, 5);

  const categoryLabel =
    comic.category === 'hanman' ? '韩漫' : comic.category === 'riman' ? '日漫' : '国漫';

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - 女神漫画`,
    description: comic.description,
    author: {
      '@type': 'Person',
      name: comic.author,
    },
    datePublished: comic.chapters[comic.chapters.length - 1]?.date || comic.updateDate,
    dateModified: comic.updateDate,
    image: comic.cover,
    publisher: {
      '@type': 'Organization',
      name: siteInfo.name,
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${siteInfo.url}/comic/${comic.id}`,
    },
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <Navbar />
      <main className="pt-16">
        <div className="max-w-[1200px] mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: categoryLabel, href: `/category/${comic.category}` },
              { label: comic.title },
            ]}
          />

          <article className="py-8">
            <div className="flex flex-col md:flex-row gap-8">
              <div className="flex-shrink-0 w-full md:w-[300px]">
                <div className="rounded-xl overflow-hidden aspect-[3/4] md:aspect-[3/4]">
                  <img
                    src={comic.cover}
                    alt={`${comic.title} - 女神漫画在线阅读`}
                    title={`${comic.title} - 女神漫画免费阅读`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1">
                <div className="flex items-center gap-2 mb-3 flex-wrap">
                  <span className={`px-3 py-1 text-xs font-medium rounded-full whitespace-nowrap ${
                    comic.status === 'ongoing'
                      ? 'bg-accent-100 text-accent-700'
                      : 'bg-secondary-100 text-secondary-700'
                  }`}>
                    {comic.status === 'ongoing' ? '连载中' : '已完结'}
                  </span>
                  <span className="px-3 py-1 text-xs font-medium rounded-full bg-primary-100 text-primary-700 whitespace-nowrap">
                    {categoryLabel}
                  </span>
                  {comic.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 text-xs rounded-full bg-background-200 text-foreground-600 whitespace-nowrap"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">
                  {comic.title}
                </h1>
                <p className="text-sm text-foreground-500 mb-4">作者：{comic.author}</p>

                <div className="flex items-center gap-6 mb-6">
                  <div className="flex items-center gap-1">
                    <i className="ri-star-fill text-lg text-primary-500"></i>
                    <span className="text-xl font-bold text-foreground-900">{comic.rating}</span>
                    <span className="text-xs text-foreground-400">/ 10</span>
                  </div>
                  <div className="flex items-center gap-1 text-sm text-foreground-500">
                    <i className="ri-eye-line"></i>
                    <span>{(comic.views / 10000).toFixed(0)} 万阅读</span>
                  </div>
                  <div className="flex items-center gap-1 text-sm text-foreground-500">
                    <i className="ri-calendar-line"></i>
                    <span>更新于 {comic.updateDate}</span>
                  </div>
                </div>

                <div className="bg-background-100 rounded-xl p-5 mb-6">
                  <h3 className="text-sm font-semibold text-foreground-900 mb-3">作品简介</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{comic.description}</p>
                </div>

                <Link
                  to={`/comic/${comic.id}`}
                  className="inline-flex items-center gap-2 px-8 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap mb-8"
                  onClick={(e) => {
                    e.preventDefault();
                    const chapterList = document.getElementById('chapter-list');
                    chapterList?.scrollIntoView({ behavior: 'smooth' });
                  }}
                >
                  <i className="ri-play-circle-line text-lg"></i>
                  开始阅读
                </Link>
              </div>
            </div>

            <section id="chapter-list" className="mt-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-1 h-5 bg-primary-500 rounded-full" />
                <h2 className="text-xl font-heading font-bold text-foreground-900">
                  章节目录
                </h2>
                <span className="text-sm text-foreground-400">
                  共 {comic.chapters.length} 话
                </span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                {comic.chapters.map((chapter) => (
                  <div
                    key={chapter.id}
                    className="flex items-center justify-between p-3 rounded-lg bg-background-100 hover:bg-primary-50 transition-colors cursor-pointer group"
                  >
                    <span className="text-sm text-foreground-700 group-hover:text-primary-700 transition-colors line-clamp-1">
                      {chapter.title}
                    </span>
                    <span className="text-xs text-foreground-400 flex-shrink-0 ml-2">{chapter.date}</span>
                  </div>
                ))}
              </div>
            </section>

            {relatedComics.length > 0 && (
              <section className="mt-14">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-1 h-5 bg-primary-500 rounded-full" />
                  <h2 className="text-xl font-heading font-bold text-foreground-900">
                    相关推荐
                  </h2>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                  {relatedComics.map((c) => (
                    <ComicCard key={c.id} comic={c} />
                  ))}
                </div>
              </section>
            )}
          </article>
        </div>
      </main>
      <Footer />
    </>
  );
}